/**
 * Attachment Storage Manager (Day-15 Module 9)
 * 
 * Handles storing attachments locally for offline sync
 * 
 * Upload States:
 * - PENDING: Queued for upload
 * - UPLOADING: Currently uploading
 * - SYNCED: Successfully uploaded to server
 * - FAILED: Upload failed
 * 
 * Use Cases:
 * - Incident photos
 * - Maintenance photos
 * - Medication proof
 * - Document uploads
 * - Compliance evidence
 */

import {
  STORES,
  addToStore,
  updateInStore,
  getAllFromStore,
  getFromStore,
  deleteFromStore,
  getByIndex,
  generateOfflineId,
} from './storage';

// ─────────────────────────────────────────────────────────────────
// Attachment Status Types
// ─────────────────────────────────────────────────────────────────

export const ATTACHMENT_STATUS = {
  PENDING: 'PENDING',
  UPLOADING: 'UPLOADING',
  SYNCED: 'SYNCED',
  FAILED: 'FAILED',
};

// ─────────────────────────────────────────────────────────────────
// Entity Types (what the attachment is for)
// ─────────────────────────────────────────────────────────────────

export const ATTACHMENT_ENTITY_TYPES = {
  INCIDENT: 'INCIDENT',
  PROGRESS_NOTE: 'PROGRESS_NOTE',
  MAINTENANCE: 'MAINTENANCE',
  MEDICATION: 'MEDICATION',
  DOCUMENT: 'DOCUMENT',
  COMPLIANCE: 'COMPLIANCE',
  HANDOVER: 'HANDOVER',
  TASK: 'TASK',
};

// ─────────────────────────────────────────────────────────────────
// Attachment Queue Operations
// ─────────────────────────────────────────────────────────────────

/**
 * Store an attachment locally for later upload
 * @param {Object} params - Attachment parameters
 * @param {string} params.filename - Original filename
 * @param {string} params.mimeType - MIME type
 * @param {number} params.size - File size in bytes
 * @param {string} params.dataUrl - Base64 data URL of the file
 * @param {string} params.thumbnailUrl - Base64 thumbnail data URL (for images)
 * @param {string} params.entityType - Type of entity (INCIDENT, MAINTENANCE, etc.)
 * @param {string} params.entityId - ID of the related entity (may be offline ID)
 * @param {string} params.queueItemId - ID of the related queue item (for linking)
 * @returns {Promise<Object>} - The stored attachment record
 */
export async function storeAttachment({
  filename,
  mimeType,
  size,
  dataUrl,
  thumbnailUrl,
  entityType,
  entityId,
  queueItemId,
  metadata = {},
}) {
  const attachment = {
    id: generateOfflineId(),
    filename,
    mimeType,
    size,
    dataUrl,
    thumbnailUrl,
    entityType,
    entityId,
    queueItemId,
    metadata,
    status: ATTACHMENT_STATUS.PENDING,
    retryCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  await addToStore(STORES.ATTACHMENTS, attachment);
  
  // Dispatch event for UI updates
  window.dispatchEvent(new CustomEvent('attachmentQueueUpdated', { detail: { attachment } }));
  
  return attachment;
}

/**
 * Get all pending attachments
 */
export async function getPendingAttachments() {
  const all = await getAllFromStore(STORES.ATTACHMENTS);
  return all.filter(a => a.status === ATTACHMENT_STATUS.PENDING);
}

/**
 * Get attachments by entity
 */
export async function getAttachmentsByEntity(entityType, entityId) {
  const all = await getAllFromStore(STORES.ATTACHMENTS);
  return all.filter(a => a.entityType === entityType && a.entityId === entityId);
}

/**
 * Get attachments by queue item ID
 */
export async function getAttachmentsByQueueItem(queueItemId) {
  return getByIndex(STORES.ATTACHMENTS, 'queueItemId', queueItemId);
}

/**
 * Get a single attachment by ID
 */
export async function getAttachment(id) {
  return getFromStore(STORES.ATTACHMENTS, id);
}

/**
 * Update attachment status
 */
export async function updateAttachmentStatus(id, status, extra = {}) {
  const attachment = await getFromStore(STORES.ATTACHMENTS, id);
  if (!attachment) {
    throw new Error(`Attachment ${id} not found`);
  }

  const updated = {
    ...attachment,
    status,
    ...extra,
    updatedAt: new Date().toISOString(),
  };

  await updateInStore(STORES.ATTACHMENTS, updated);
  window.dispatchEvent(new CustomEvent('attachmentQueueUpdated', { detail: { attachment: updated } }));
  
  return updated;
}

/**
 * Mark attachment as uploading
 */
export async function markAttachmentUploading(id) {
  return updateAttachmentStatus(id, ATTACHMENT_STATUS.UPLOADING);
}

/**
 * Mark attachment as synced
 */
export async function markAttachmentSynced(id, serverId, serverPath) {
  return updateAttachmentStatus(id, ATTACHMENT_STATUS.SYNCED, {
    serverId,
    serverPath,
    syncedAt: new Date().toISOString(),
  });
}

/**
 * Mark attachment as failed
 */
export async function markAttachmentFailed(id, error) {
  const attachment = await getFromStore(STORES.ATTACHMENTS, id);
  return updateAttachmentStatus(id, ATTACHMENT_STATUS.FAILED, {
    lastError: typeof error === 'string' ? error : error.message || 'Unknown error',
    retryCount: (attachment?.retryCount || 0) + 1,
    lastFailedAt: new Date().toISOString(),
  });
}

/**
 * Retry a failed attachment
 */
export async function retryAttachment(id) {
  return updateAttachmentStatus(id, ATTACHMENT_STATUS.PENDING, {
    lastError: null,
  });
}

/**
 * Delete an attachment
 */
export async function deleteAttachment(id) {
  await deleteFromStore(STORES.ATTACHMENTS, id);
  window.dispatchEvent(new CustomEvent('attachmentQueueUpdated'));
}

/**
 * Delete all attachments for an entity
 */
export async function deleteAttachmentsByEntity(entityType, entityId) {
  const attachments = await getAttachmentsByEntity(entityType, entityId);
  for (const attachment of attachments) {
    await deleteFromStore(STORES.ATTACHMENTS, attachment.id);
  }
  window.dispatchEvent(new CustomEvent('attachmentQueueUpdated'));
  return attachments.length;
}

/**
 * Get attachment statistics
 */
export async function getAttachmentStats() {
  const all = await getAllFromStore(STORES.ATTACHMENTS);
  return {
    total: all.length,
    pending: all.filter(a => a.status === ATTACHMENT_STATUS.PENDING).length,
    uploading: all.filter(a => a.status === ATTACHMENT_STATUS.UPLOADING).length,
    synced: all.filter(a => a.status === ATTACHMENT_STATUS.SYNCED).length,
    failed: all.filter(a => a.status === ATTACHMENT_STATUS.FAILED).length,
    totalSize: all.reduce((sum, a) => sum + (a.size || 0), 0),
  };
}

/**
 * Cleanup synced attachments (remove old synced attachments to save space)
 */
export async function cleanupSyncedAttachments(olderThanDays = 7) {
  const all = await getAllFromStore(STORES.ATTACHMENTS);
  const cutoff = Date.now() - olderThanDays * 24 * 60 * 60 * 1000;
  
  let cleaned = 0;
  for (const attachment of all) {
    if (
      attachment.status === ATTACHMENT_STATUS.SYNCED &&
      new Date(attachment.syncedAt || attachment.createdAt).getTime() < cutoff
    ) {
      // Keep metadata but remove the heavy dataUrl
      await updateInStore(STORES.ATTACHMENTS, {
        ...attachment,
        dataUrl: null,
        thumbnailUrl: null,
        cleaned: true,
      });
      cleaned++;
    }
  }
  
  return cleaned;
}

/**
 * Get human-readable status label
 */
export function getAttachmentStatusLabel(status) {
  const labels = {
    [ATTACHMENT_STATUS.PENDING]: 'Waiting to upload',
    [ATTACHMENT_STATUS.UPLOADING]: 'Uploading...',
    [ATTACHMENT_STATUS.SYNCED]: 'Uploaded',
    [ATTACHMENT_STATUS.FAILED]: 'Upload failed',
  };
  return labels[status] || status;
}

/**
 * Get status color for UI
 */
export function getAttachmentStatusColor(status) {
  const colors = {
    [ATTACHMENT_STATUS.PENDING]: 'amber',
    [ATTACHMENT_STATUS.UPLOADING]: 'blue',
    [ATTACHMENT_STATUS.SYNCED]: 'green',
    [ATTACHMENT_STATUS.FAILED]: 'red',
  };
  return colors[status] || 'gray';
}

export default {
  ATTACHMENT_STATUS,
  ATTACHMENT_ENTITY_TYPES,
  storeAttachment,
  getPendingAttachments,
  getAttachmentsByEntity,
  getAttachmentsByQueueItem,
  getAttachment,
  updateAttachmentStatus,
  markAttachmentUploading,
  markAttachmentSynced,
  markAttachmentFailed,
  retryAttachment,
  deleteAttachment,
  deleteAttachmentsByEntity,
  getAttachmentStats,
  cleanupSyncedAttachments,
  getAttachmentStatusLabel,
  getAttachmentStatusColor,
};
